// Scherm 9 — de trendlijst.
//
// Vorm uit de dummy (V1 r. 81-95 + Row/Grp r. 43-51): filterkaart, tabelkaart
// met kopregel van 34px, groepen met een inklapbare kop, rijen van 44px,
// paginering onderaan, detailpaneel van rechts.
//
// Bindend uit W8 §6:
//  1. de lijst OPENT op het kernbeeld — dragende vaktrends
//  2. de volgorde komt uit de uitlichtrubriek en de REDEN is opvraagbaar
//  3. contexttrends staan als bewijsregel onder hun trend
//  4. arbeidsmarkttrends horen op scherm 14
//
// Owner-feedback (opname 2026-08-24):
//  · 1:00-1:20 "dit is echt duidelijk: een grote trend en alles eronder kun je
//    inklappen" -> de groepskop uit de dummy is terug, dichtgeklapt by default
//  · 1:26-1:45 "al die pillen zijn te veel informatie; categorie, trending,
//    impact en urgentie zijn prima" -> de rijen dragen geen badges meer. De
//    reden bij de score is niet verdwenen maar verhuisd: hij hangt onder de
//    score (tooltip) en staat voluit in het detailpaneel.
//  · 0:10-0:25 gekozen filters als chips op een EIGEN regel onder de filters

import { lazy, Suspense, useEffect, useState } from "react";

import { BewijsCel } from "../components/Bewijs";
import { EmptyList, LoadError } from "../components/Empty";
import { FilterChips, type Chip } from "../components/FilterChips";
import { FilterMenu } from "../components/FilterMenu";
import { OnderwerpFilter } from "../components/OnderwerpFilter";
import { TrendDetail } from "../components/TrendDetail";
import {
  Card,
  FilterBar,
  PageHeader,
  Pager,
  ScoreCell,
  SearchField,
  SlidePanel,
  SortSelect,
  TableHead,
} from "../components/ui";
import type { Trend } from "../contracts";
import { api } from "../lib/api";
import { useMeta } from "../lib/meta";
import { bedrijfstakSectoren, gemetenZin, usePropositieFilter } from "../lib/propositieFilter";
import { useBaseline } from "../lib/rounds";
import { useStartSector } from "../lib/sectorkeuze";
import { useDebounced, useResource } from "../lib/useResource";
import { alsLijst, alsTekst, type Voorzet } from "../lib/voorzet";
import { trendlaagAan } from "../nieuw/trendlaag/schakelaar";
import { color, size, weight } from "../tokens";

// De trendlaag (trend › ontwikkeling › bevinding) alleen met ?trendlaag=1, net als
// in de nieuwe weergave (nieuw/paginas/Trends.tsx). Los te laden: zonder de
// schakelaar laadt er niets extra en blijft deze pagina precies zoals hij was.
const TrendlaagLos = lazy(() => import("../nieuw/trendlaag/los"));

// De zesde kolom is 60px: het 32×3-spoor van de bewijscel plus het aantal
// bronnen ernaast paste niet in de 48px van de kale telling.
const COLS = "1fr 190px 72px 72px 72px 60px 48px";
// "Bron" wordt "Bewijs" zodra de lijst bewijs draagt (methode v3): dan staat
// er per trend hoe ver de opstellers samen boven de drempel staan. Zonder
// bewijs blijft de kop en de kale telling zoals hij was.
const LABELS = ["Trend", "Categorie", "Trending", "Impact", "Urgentie", "Bron", "Skills"] as const;
const LABELS_BEWIJS = ["Trend", "Categorie", "Trending", "Impact", "Urgentie", "Bewijs", "Skills"] as const;
const PAGE_SIZE = 15;
// De vaste kolommen (514 px) plus de randen en een leesbare titelkolom.
const TABEL_MIN = 780;

const LEVEL_COLOR: Record<string, string> = {
  Macro: "#111827",
  National: "#2563eb",
  Sector: "#059669",
};

const SIGNAL_OPTIONS = ["Sterk (8+)", "Gemiddeld (6-8)", "Zwak (<6)"] as const;
const SIGNAL_PARAM: Record<string, string> = {
  "Sterk (8+)": "sterk",
  "Gemiddeld (6-8)": "gemiddeld",
  "Zwak (<6)": "zwak",
};
// De abstractiehoogte in gewone taal. De owner vroeg wat "SEC" betekent en hoe
// dat zich tot "MAC" verhoudt (opname 2026-08-24) — de API-waarden blijven
// Macro|National|Sector, het scherm zegt wat ze betekenen.
const LEVEL_OPTIONS = [
  "Macro — internationaal",
  "Nationaal — Nederland",
  "Sector — één bedrijfstak of beroepsgroep",
] as const;
const LEVEL_PARAM: Record<string, string> = {
  "Macro — internationaal": "Macro",
  "Nationaal — Nederland": "National",
  "Sector — één bedrijfstak of beroepsgroep": "Sector",
};
const LEVEL_BADGE: Record<string, { kort: string; uitleg: string }> = {
  Macro: { kort: "MACRO", uitleg: "Macro: een internationaal beeld (EU, OESO, mondiaal)." },
  National: { kort: "NL", uitleg: "Nationaal: gaat over Nederland als geheel." },
  Sector: {
    kort: "SECTOR",
    uitleg: "Sector: over één bedrijfstak, regio of beroepsgroep — het meest specifieke niveau.",
  },
};

// De soort-as (W8 scherm 9/14): vaktrends gaan over het WERK, arbeidsmarkttrends
// over de MARKT eromheen. De owner wil dat als filter zien, met vaktrends als
// vertrekpunt: "het is onduidelijk waar de kerntrends staan."
// DE DERDE STAND IS ER BIJGEKOMEN MET DE CHAT-DOORKLIK. De chat kan over BEIDE
// soorten tegelijk tellen ("hoeveel trends over cybersecurity" telt vak- én
// arbeidsmarkttrends), en dan moet dit scherm dat getal ook kunnen tonen.
// Stond hier alleen vak/arbeidsmarkt, dan liet de doorklik een kleiner aantal
// zien dan de chat noemde en was het getal niet meer na te tellen. De
// standaard blijft "Vaktrends": dat is het kernbeeld waar het scherm op opent.
const KIND_OPTIONS = [
  { key: "vakgebied", label: "Vaktrends", hint: "Wat er verandert in het werk zelf." },
  {
    key: "arbeidsmarktobservatie",
    label: "Arbeidsmarkt",
    hint: "Wat er verandert aan de markt eromheen: vraag, aanbod, krapte, beleid.",
  },
  { key: "", label: "Alles", hint: "Beide soorten samen, zoals de chat ze telt." },
] as const;

// Sorteren. "Uitgelicht" is de uitlichtrubriek uit W1a en blijft de standaard:
// die zet de best onderbouwde en meest bewegende trends bovenaan, mét een reden
// die je op de score kunt opvragen.
const SORTS: { key: string; label: string; hint: string }[] = [
  { key: "uitgelicht", label: "Uitgelicht", hint: "Volgorde van de uitlichtrubriek: bewijskracht, breedte, versheid en beweging samen." },
  { key: "trending", label: "Trending", hint: "Hoe sterk het signaal nu is." },
  { key: "impact", label: "Impact", hint: "Hoe ingrijpend het voor het werk is." },
  { key: "urgentie", label: "Urgentie", hint: "Hoe snel het speelt." },
  { key: "bronnen", label: "Meeste bronnen", hint: "Hoeveel onafhankelijke bronnen het dragen." },
  { key: "nieuwste", label: "Nieuwste bewijs", hint: "Waar het jongste bewijs onder ligt." },
];
// Methode v3: "Meeste bronnen" wordt "Sterkste bewijs" en stuurt de
// API-sleutel `bewijs` (afronding 01-10): de API ordent dan over ALLE pagina's
// op bewijspunten (drempel 3), daarna op bronaantal, daarna op titel. Onder
// v1 bestaat de sleutel niet (hij zou daar terugvallen op "uitgelicht"), dus
// zonder v3 blijft de lijst "Meeste bronnen" met de sleutel `bronnen` sturen.
const SORTS_BEWIJS = SORTS.map((s) =>
  s.key === "bronnen"
    ? { key: "bewijs", label: "Sterkste bewijs", hint: "Hoeveel bewijspunten de opstellers samen halen (drempel 3); daarna het aantal bronnen." }
    : s,
);

// Het detailniveau: hoe grof kijk je? Dit is GEEN filter — er verdwijnt niets.
// Onderliggende trends schuiven in hun ouder en zijn daar een klik verderop.
type DetailLevel = "super" | "kern" | "alles";
const DETAIL_LEVELS: { key: DetailLevel; label: string; hint: string }[] = [
  {
    key: "super",
    label: "Supertrends",
    hint: "Alleen trends die andere trends bundelen. De rest zit eronder.",
  },
  {
    key: "kern",
    label: "Kernbeeld",
    hint: "Elke dragende trend als eigen regel; onderbouwing ingeklapt.",
  },
  {
    key: "alles",
    label: "Alles uitgeklapt",
    hint: "Ook de onderbouwende trends staan open onder hun ouder.",
  },
];

function isNew(iso: string | undefined): boolean {
  if (!iso) return false;
  const d = Date.parse(iso);
  if (Number.isNaN(d)) return false;
  return (Date.now() - d) / 864e5 <= 60;
}

function Row({
  trend,
  indent,
  onOpen,
  showNew,
}: {
  trend: Trend;
  indent: boolean;
  onOpen: (t: Trend) => void;
  /** In ronde 1 is elke trend nieuw; dan zegt de badge niets en tonen we hem niet. */
  showNew: boolean;
}) {
  const [hover, setHover] = useState(false);
  // De reden achter de volgorde verdwijnt niet, hij wordt opvraagbaar: hij hangt
  // als toelichting aan de trending-score en staat voluit in het detailpaneel.
  const badges = trend.uitlichtBadges ?? [];
  const reden = badges.length ? `Uitgelicht omdat: ${badges.join(" · ")}` : undefined;

  return (
    <div
      onClick={() => onOpen(trend)}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      style={{
        display: "grid",
        gridTemplateColumns: indent ? `24px ${COLS}` : COLS,
        gap: 0,
        alignItems: "center",
        padding: "0 16px",
        minHeight: 44,
        cursor: "pointer",
        background: hover ? color.surfaceAlt : color.surface,
        transition: "background 0.1s",
        borderBottom: `1px solid ${color.lineFaint}`,
      }}
    >
      {indent && (
        <div style={{ display: "flex", justifyContent: "center" }}>
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" style={{ opacity: 0.2 }}>
            <path
              d="M3 1v6a2 2 0 002 2h4"
              stroke={color.faint}
              strokeWidth="1.5"
              strokeLinecap="round"
            />
          </svg>
        </div>
      )}
      <div style={{ display: "flex", alignItems: "center", gap: 7, minWidth: 0, paddingRight: 8 }}>
        <span
          title={LEVEL_BADGE[trend.level]?.uitleg ?? trend.level}
          style={{
            fontSize: 9,
            fontWeight: weight.bold,
            padding: "2px 5px",
            borderRadius: 3,
            background: LEVEL_COLOR[trend.level] ?? color.soft,
            color: "white",
            flexShrink: 0,
            letterSpacing: "0.04em",
          }}
        >
          {LEVEL_BADGE[trend.level]?.kort ?? trend.level}
        </span>
        <span
          style={{
            fontSize: size.meta,
            fontWeight: indent ? 400 : weight.regular,
            color: color.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {trend.displayTitle || trend.title}
        </span>
        {showNew && isNew(trend.firstDetected) && (
          <span
            title="Voor het eerst gezien in de laatste 60 dagen."
            style={{
              fontSize: 8,
              fontWeight: weight.bold,
              padding: "1px 4px",
              borderRadius: 3,
              background: "#fef3c7",
              color: "#92400e",
              flexShrink: 0,
            }}
          >
            NIEUW
          </span>
        )}
      </div>
      <span
        title={
          trend.categoryProposed
            ? `Voorgestelde categorie, nog niet vastgesteld: ${trend.categoryProposed}`
            : trend.category
        }
        style={{
          fontSize: 11,
          color: trend.categoryProposed ? color.accentAmber : color.soft,
          fontStyle: trend.categoryProposed ? "italic" : "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: 10,
        }}
      >
        {trend.category ?? trend.categoryProposed ?? "—"}
      </span>
      <ScoreCell value={trend.scores?.trending} tone={color.accentBlue} title={reden} />
      <ScoreCell value={trend.scores?.impact} tone={color.accentGreen} />
      <ScoreCell value={trend.scores?.urgency} tone={color.accentAmber} />
      {/* Methode v3: het bewijs als 32×3-spoor (punten tegen de drempel) met
          het bronaantal ernaast; de tooltip zegt wie bevestigt en wat er
          mist. Zonder bewijs de kale telling, zoals voorheen. */}
      {trend.bewijs ? (
        <BewijsCel bewijs={trend.bewijs} bronnen={trend.sourceCount} />
      ) : (
        <span style={{ fontSize: 11, color: color.soft, textAlign: "center" }}>
          {trend.sourceCount}
        </span>
      )}
      <span style={{ fontSize: 11, color: color.soft, textAlign: "center" }}>
        {trend.skillCount}
      </span>
    </div>
  );
}

/**
 * Een groep: de dikke trendkop met chevron en telling, en daaronder de trend
 * zelf plus de trends die hem onderbouwen. Vorm uit de dummy (V1 r. 51), waar
 * de owner naar wees: "een grote trend en alles eronder kun je inklappen".
 */
function Group({
  trend,
  items,
  onOpen,
  showNew,
  expanded,
}: {
  trend: Trend;
  items: Trend[];
  onOpen: (t: Trend) => void;
  showNew: boolean;
  expanded: boolean;
}) {
  const [open, setOpen] = useState(expanded);
  useEffect(() => setOpen(expanded), [expanded]);

  // Zonder onderliggende trends valt er niets in te klappen: één gewone regel.
  if (items.length === 0) {
    return <Row trend={trend} indent={false} onOpen={onOpen} showNew={showNew} />;
  }

  return (
    <div>
      <div
        onClick={() => setOpen(!open)}
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "0 16px",
          height: 38,
          cursor: "pointer",
          background: color.surfaceQuote,
          borderBottom: `1px solid ${color.lineSoft}`,
          borderLeft: `3px solid ${LEVEL_COLOR[trend.level] ?? color.soft}`,
        }}
      >
        <svg
          width="9"
          height="9"
          viewBox="0 0 8 8"
          fill="none"
          style={{
            transition: "transform 0.15s",
            transform: open ? "rotate(90deg)" : "none",
            opacity: 0.45,
            flexShrink: 0,
          }}
        >
          <path
            d="M2 0l4 4-4 4"
            stroke={color.body}
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
        <span
          style={{
            fontSize: size.meta,
            fontWeight: weight.bold,
            color: color.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            flex: 1,
          }}
        >
          {trend.displayTitle || trend.title}
        </span>
        <span
          title="Deze trend plus de trends die hem onderbouwen."
          style={{
            fontSize: 10,
            color: color.faint,
            background: color.line,
            padding: "1px 7px",
            borderRadius: 8,
            flexShrink: 0,
          }}
        >
          {1 + items.length}
        </span>
      </div>
      {open && (
        <>
          <Row trend={trend} indent={false} onOpen={onOpen} showNew={showNew} />
          {items.map((child) => (
            <Row key={child.id} trend={child} indent onOpen={onOpen} showNew={showNew} />
          ))}
        </>
      )}
    </div>
  );
}

/** Een treffer uit de zoekoverlay: die hoort hier open te gaan.
 *  Vóór owner-review ronde 4 werkte dat alleen voor trends; een skill of bron
 *  aanklikken deed niets. */
type TrendsPageProps = {
  openen?: Trend | null;
  /** Een filter dat van buiten wordt voorgezet (chat-doorklik, redactiekaart). */
  voorzet?: Voorzet | null;
};

export function TrendsPage(props: TrendsPageProps = {}) {
  // Pilot (samenvoeging release 1 + trendlaag): de startsector uit /api/mij,
  // niet vast ICT (die is verborgen). Zonder app (een test) is het ICT.
  const start = useStartSector();
  if (trendlaagAan())
    return (
      <Suspense fallback={<p style={{ color: color.muted }}>Trends laden…</p>}>
        <TrendlaagLos
          openen={props.openen}
          voorzet={props.voorzet}
          sector={(props.voorzet && alsTekst(props.voorzet, "sector")) || start}
        />
      </Suspense>
    );
  return <TrendsOud {...props} />;
}

function TrendsOud({ openen, voorzet }: TrendsPageProps) {
  const baseline = useBaseline();
  const meta = useMeta();
  const [page, setPage] = useState(1);
  const [signals, setSignals] = useState<string[]>([]);
  const [levels, setLevels] = useState<string[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  // Filter Propositie (ADR 2026-10-23-ict-los-platform): alleen bij twee of
  // meer zichtbare proposities; dan opent het scherm op de eigen propositie.
  const prop = usePropositieFilter(meta);
  const [categories, setCategories] = useState<string[]>([]);
  const [detail, setDetail] = useState<DetailLevel>("kern");
  const [kind, setKind] = useState<string>("vakgebied");
  const [sorteer, setSorteer] = useState<string>("uitgelicht");
  const [zoek, setZoek] = useState("");
  const [onderwerpen, setOnderwerpen] = useState<string[]>([]);
  const [selected, setSelected] = useState<Trend | null>(null);

  const signaal = signals.map((s) => SIGNAL_PARAM[s]).filter(Boolean) as string[];
  const niveaus = levels.map((l) => LEVEL_PARAM[l]).filter(Boolean) as string[];

  // De assen komen uit de API (backlog 30: één as, één woordenlijst).
  const sectorOptions = bedrijfstakSectoren(meta).map((s) => s.label);
  const sectorDisabled = Object.fromEntries(
    bedrijfstakSectoren(meta)
      .filter((s) => !s.active)
      .map((s) => [s.label, "Nog geen corpus: deze sector is voorbereid maar niet gemeten."]),
  );
  const sectorKey = (label: string | undefined) =>
    (meta?.sectors ?? []).find((s) => s.label === label)?.key;
  const categoryOptions = (meta?.categories ?? [])
    .filter((c) => c.kind !== "arbeidsmarktobservatie")
    .map((c) => c.name);

  // Wacht tot je even stil bent: anders vuurt élke toetsaanslag een ronde.
  const zoekTraag = useDebounced(zoek, 300);

  // Een treffer uit de zoekoverlay opent hier zijn detailpaneel.
  useEffect(() => {
    if (openen) setSelected(openen);
  }, [openen]);

  // Een voorgezet filter vervangt de huidige keuze en wist de rest, zodat het
  // getal in de kop hetzelfde is als het getal waar je op klikte.
  useEffect(() => {
    if (!voorzet) return;
    setCategories(alsLijst(voorzet, "category"));
    // De niveau-as staat op het scherm in gewone taal en in de API als
    // Macro|National|Sector; een voorzet komt van de API-kant en moet dus terug
    // vertaald worden, anders zet hij een label dat geen enkel vinkje raakt.
    const naarLabel = Object.fromEntries(
      Object.entries(LEVEL_PARAM).map(([label, param]) => [param, label]),
    );
    setLevels(
      alsLijst(voorzet, "level")
        .map((p) => naarLabel[p])
        .filter(Boolean) as string[],
    );
    setKind(alsTekst(voorzet, "kind"));
    setZoek(alsTekst(voorzet, "zoek"));
    setSignals([]);
    setSectors([]);
    setOnderwerpen([]);
    setPage(1);
  }, [voorzet]);

  // Methode v3 (bronlabels in /api/meta): de sortering "Sterkste bewijs"
  // bestaat. Valt de methode weg terwijl hij gekozen staat, dan stuurt het
  // scherm de oude sleutel `bronnen`.
  const methodeV3 = Boolean(meta?.bronlabels);
  const sorteerEcht = sorteer === "bewijs" && !methodeV3 ? "bronnen" : sorteer;
  const list = useResource(
    () =>
      api.trends({
        page,
        pageSize: PAGE_SIZE,
        laag: "drager",
        kind,
        signaal,
        level: niveaus,
        category: categories,
        sector: [...(sectors.map(sectorKey).filter(Boolean) as string[]), ...prop.sleutels],
        detail,
        sorteer: sorteerEcht,
        topic: onderwerpen,
        search: zoekTraag || undefined,
      }),
    [
      page,
      signals.join("|"),
      levels.join("|"),
      categories.join("|"),
      sectors.join("|"),
      prop.gekozen.join("|"),
      detail,
      kind,
      sorteerEcht,
      onderwerpen.join("|"),
      zoekTraag,
      meta,
    ],
    meta !== null && prop.klaar,
  );

  const facets = list.data?.facets;
  const countsFor = (axis: "category" | "level" | "sector" | "signaal") =>
    Object.fromEntries((facets?.[axis] ?? []).map((f) => [f.value, f.count]));
  const categoryCounts = countsFor("category");
  const levelCountsByKey = countsFor("level");
  const levelCounts = Object.fromEntries(
    LEVEL_OPTIONS.map((label) => [label, levelCountsByKey[LEVEL_PARAM[label]!] ?? 0]),
  );
  const sectorCountsByKey = countsFor("sector");
  const signalCountsByKey = countsFor("signaal");
  const sectorCounts = Object.fromEntries(
    (meta?.sectors ?? []).map((s) => [s.label, sectorCountsByKey[s.key] ?? 0]),
  );
  const signalCounts = Object.fromEntries(
    SIGNAL_OPTIONS.map((label) => [label, signalCountsByKey[SIGNAL_PARAM[label]!] ?? 0]),
  );

  const chips: Chip[] = [
    ...prop.gekozen.map((v) => ({ axis: "Propositie", value: v, label: `Propositie: ${v}` })),
    ...sectors.map((v) => ({ axis: "Sector", value: v, label: v })),
    ...categories.map((v) => ({ axis: "Categorie", value: v, label: v })),
    ...signals.map((v) => ({ axis: "Signaal", value: v, label: v })),
    ...levels.map((v) => ({ axis: "Niveau", value: v, label: v })),
    ...onderwerpen.map((v) => ({ axis: "Onderwerp", value: v, label: `Onderwerp: ${v}` })),
    ...(zoek ? [{ axis: "Zoek", value: zoek, label: `Zoekt op "${zoek}"` }] : []),
  ];
  const removeChip = (chip: Chip) => {
    const drop = (xs: string[]) => xs.filter((x) => x !== chip.value);
    if (chip.axis === "Propositie") prop.zet(drop(prop.gekozen));
    if (chip.axis === "Sector") setSectors(drop(sectors));
    if (chip.axis === "Categorie") setCategories(drop(categories));
    if (chip.axis === "Signaal") setSignals(drop(signals));
    if (chip.axis === "Niveau") setLevels(drop(levels));
    if (chip.axis === "Onderwerp") setOnderwerpen(drop(onderwerpen));
    if (chip.axis === "Zoek") setZoek("");
    setPage(1);
  };
  const clear = () => {
    setSignals([]);
    setLevels([]);
    setSectors([]);
    prop.terug();
    setCategories([]);
    setOnderwerpen([]);
    setZoek("");
    setPage(1);
  };

  // De volgorde is die van de API, ook onder "Sterkste bewijs": daar sorteert
  // de API zelf op bewijspunten, over de pagina's heen.
  const groups = list.data?.groups ?? [];
  const heeftBewijs = groups.some((g) => Boolean(g.trend.bewijs));
  const childrenOf = (t: Trend) => groups.find((g) => g.trend.id === t.id)?.children ?? [];
  const parentOf = (t: Trend) =>
    t.parentId ? (groups.find((g) => g.trend.id === t.parentId)?.trend ?? null) : null;

  const total = list.data?.total ?? 0;
  // Zie SkillsPage: hoeveel trends zélf aan het filter voldoen.
  const treffers = list.data?.totalMatching ?? total;
  const totalGroups = list.data?.totalGroups ?? groups.length;
  const totalPages = Math.max(1, Math.ceil(totalGroups / PAGE_SIZE));

  return (
    <div>
      <PageHeader
        title="Trends"
        count={
          list.loading
            ? "laden…"
            : `${total.toLocaleString("nl-NL")} ` +
              `${
                kind === "vakgebied"
                  ? "vaktrends"
                  : kind === "arbeidsmarktobservatie"
                    ? "arbeidsmarkttrends"
                    : "trends (vak én arbeidsmarkt)"
              } in beeld` +
              (treffers !== total
                ? ` · ${treffers.toLocaleString("nl-NL")} ${
                    treffers === 1 ? "voldoet" : "voldoen"
                  } aan het filter, de rest staat eronder als onderbouwing`
                : "") +
              (detail === "super"
                ? ` · gebundeld in ${totalGroups.toLocaleString("nl-NL")} ` +
                  `${totalGroups === 1 ? "supertrend" : "supertrends"}`
                : "") +
              ` · pagina ${page} van ${totalPages}`
        }
      />

      <FilterBar
        chips={<FilterChips chips={chips} onRemove={removeChip} onClearAll={clear} />}
      >
          {/* Filtert de trendlijst zélf (owner-besluit ronde 4). Vóór die ronde
              was dit een readOnly-knop die de zoekoverlay opende — hij zag eruit
              als een lijstfilter en was er geen. Zoeken over alle soorten heen
              zit nu in de kop, naast de chatknop. */}
          <SearchField
            value={zoek}
            onChange={(v) => {
              setZoek(v);
              setPage(1);
            }}
            placeholder="Zoek trends…"
          />
          <div style={{ width: 1, height: 24, background: color.border, flexShrink: 0 }} />
          {/* De soort-as. Was een aparte standenrij onder de balk; de owner
              wil hem als filter (bericht 2026-08-25). Eén keuze tegelijk, zoals
              "Weergave" hieronder: klik je de actieve uit, dan sta je weer op
              het kernbeeld Vaktrends. GEEN aantallen in dit menu — /api/trends
              geeft geen facet op deze as, en een verzonnen getal is erger dan
              geen getal; de kop telt wat er in beeld is. */}
          <FilterMenu
            label="Soort"
            options={KIND_OPTIONS.map((k) => k.label)}
            values={[KIND_OPTIONS.find((k) => k.key === kind)!.label]}
            note={KIND_OPTIONS.map((k) => `${k.label}: ${k.hint}`).join(" ")}
            onChange={(v) => {
              const actief = KIND_OPTIONS.find((k) => k.key === kind)!.label;
              const nieuw = v.filter((x) => x !== actief);
              const gekozen = KIND_OPTIONS.find((k) => k.label === nieuw[nieuw.length - 1]);
              setKind(gekozen ? gekozen.key : "vakgebied");
              setPage(1);
            }}
          />
          {prop.toon && (
            <FilterMenu
              label="Propositie"
              options={prop.opties}
              values={prop.gekozen}
              note="Elk scherm opent op jouw propositie."
              onChange={(v) => {
                prop.zet(v);
                setPage(1);
              }}
            />
          )}
          <FilterMenu
            label="Sector"
            options={sectorOptions}
            values={sectors}
            counts={sectorCounts}
            disabled={sectorDisabled}
            note={
              `${gemetenZin(meta)}. De andere staan klaar in de configuratie en ` +
              "kleuren mee zodra hun corpus gedraaid is."
            }
            onChange={(v) => {
              setSectors(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Categorie"
            options={categoryOptions}
            values={categories}
            counts={categoryCounts}
            onChange={(v) => {
              setCategories(v);
              setPage(1);
            }}
          />
          <OnderwerpFilter
            onderwerpen={meta?.onderwerpen ?? []}
            gekozen={onderwerpen}
            facet={countsFor("topics" as never)}
            eenheid="trends"
            onChange={(v) => {
              setOnderwerpen(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Weergave"
            options={DETAIL_LEVELS.map((l) => l.label)}
            values={[DETAIL_LEVELS.find((l) => l.key === detail)!.label]}
            onChange={(v) => {
              // Eén keuze tegelijk: de laatst aangeklikte wint, en klik je hem
              // uit dan sta je weer op het kernbeeld.
              const gekozen = v.filter((x) => x !== DETAIL_LEVELS.find((l) => l.key === detail)!.label);
              const lvl = DETAIL_LEVELS.find((l) => l.label === gekozen[gekozen.length - 1]);
              setDetail(lvl ? lvl.key : "kern");
              setPage(1);
            }}
          />
          <FilterMenu
            label="Signaal"
            options={SIGNAL_OPTIONS}
            values={signals}
            counts={signalCounts}
            onChange={(v) => {
              setSignals(v);
              setPage(1);
            }}
          />
          <FilterMenu
            label="Niveau"
            options={LEVEL_OPTIONS}
            values={levels}
            counts={levelCounts}
            onChange={(v) => {
              setLevels(v);
              setPage(1);
            }}
          />
      </FilterBar>

      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 12,
          marginBottom: 12,
          flexWrap: "wrap",
        }}
      >
        {/* Sorteren RECHTS, zoals op Skills en Bronnen (V1 r. 1750-1765).
            Owner-review ronde 4: hij vroeg er op twee schermen om, en het is
            geen schermspecifieke wens — de weergaveknoppen en de sortering zijn
            twee verschillende dingen en horen niet als één groep te lezen. */}
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginLeft: "auto" }}>
          <SortSelect
            value={sorteerEcht}
            options={(methodeV3 ? SORTS_BEWIJS : SORTS).map((s2) => [s2.key, s2.label] as const)}
            onChange={(v) => {
              setSorteer(v);
              setPage(1);
            }}
          />
        </div>
      </div>

      <Card
        style={{
          overflow: "hidden",
          // Blijft staan terwijl de nieuwe selectie onderweg is; dimt alleen.
          // Een lijst die bij elke filterklik leegloopt voelt trager dan hij is.
          opacity: list.stale ? 0.5 : 1,
          transition: "opacity 0.15s",
        }}
      >
        {/* Op een smal scherm schuift de TABEL zijwaarts binnen de kaart; de
            pagina zelf niet. Zonder deze minimumbreedte kreeg de titelkolom
            (1fr) op 390 px nul breedte en stonden er rijen zonder titel
            (gemeten 01-10). Op een breed scherm verandert er niets. */}
        <div style={{ overflowX: "auto" }}>
        <div style={{ minWidth: TABEL_MIN }}>
        <TableHead cols={COLS} labels={heeftBewijs ? LABELS_BEWIJS : LABELS} />
        {list.error ? (
          <LoadError reason={list.error} onRetry={list.reload} />
        ) : list.loading && !list.data ? (
          <div style={{ padding: "48px 0", textAlign: "center", color: color.faint }}>laden…</div>
        ) : groups.length === 0 ? (
          <EmptyList
            reason={
              chips.length > 0
                ? "Geen trends met deze filters."
                : "Geen trends gevonden — dit is een lege database, geen leeg filter."
            }
            onReset={chips.length > 0 ? clear : undefined}
          />
        ) : (
          groups.map((group) => (
            <Group
              key={group.trend.id}
              trend={group.trend}
              items={group.children}
              onOpen={setSelected}
              showNew={!baseline}
              // Filter je, dan wil je de treffers zíen: groepen staan open, zodat
              // het getal in de kop gelijk is aan wat je op de pagina kunt tellen.
              expanded={detail === "alles" || chips.length > 0}
            />
          ))
        )}
        </div>
        </div>
      </Card>

      <Pager page={page} totalPages={totalPages} onPage={setPage} />

      <SlidePanel open={selected !== null} onClose={() => setSelected(null)} terugLabel="Terug naar de lijst">
        {selected && (
          <TrendDetail
            trend={selected}
            childTrends={childrenOf(selected)}
            parent={parentOf(selected)}
            onOpenTrend={(t) => setSelected(t)}
            onClose={() => setSelected(null)}
          />
        )}
      </SlidePanel>
    </div>
  );
}
